import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Heart, Shirt, Trophy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { OPEN_SUPPORT_CONFIRM_EVENT } from "@/components/support/SupportSection";
import HerCollection from "@/components/support/HerCollection";
import {
  LIVE_COMMERCE_UI_ENABLED,
  resolveShopMode,
  type CommerceSettingsLite,
  type LiveProductLite,
  type ShopMode,
} from "@/lib/commerce";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Resolves Shop availability via the central rule in lib/commerce. Fails closed:
 * the public cannot read shop settings, and the live-products RPC does not yet
 * return rights fields, so "live" is impossible until both are extended AND the
 * master switch is turned on.
 */
export const useShopMode = (athleteId: string, adminOverride: boolean, athletePublished: boolean): ShopMode => {
  const [mode, setMode] = useState<ShopMode>(adminOverride ? "preview" : "off");
  useEffect(() => {
    let alive = true;
    (async () => {
      // RLS: only admins and the athlete herself can read this row.
      const { data } = await supabase
        .from("athlete_commerce_settings" as never)
        .select("commerce_enabled, is_test, shop_status, athlete_rights_status")
        .eq("athlete_id", athleteId)
        .maybeSingle();
      let products: LiveProductLite[] = [];
      if (LIVE_COMMERCE_UI_ENABLED) {
        const { data: live } = await supabase.rpc("get_live_merch_products" as never, { _athlete_id: athleteId } as never);
        products = Array.isArray(live) ? (live as LiveProductLite[]) : [];
      }
      if (alive)
        setMode(resolveShopMode({ adminOverride, settings: data as CommerceSettingsLite, athletePublished, products }));
    })();
    return () => {
      alive = false;
    };
  }, [athleteId, adminOverride, athletePublished]);
  return mode;
};

type Props = {
  firstName: string;
  athleteId: string;
  slug: string;
  /** "both" → two-option chooser; "shop_only" → straight into Her Collection. */
  experience: "both" | "shop_only";
  /** Admin preview / test shop state. */
  preview: boolean;
  /** Decided by shouldTrackCommerceEvents — false in Admin Preview and for test shops. */
  trackEvents: boolean;
};

const BackHerGameChooser = ({ firstName, athleteId, slug, experience, preview, trackEvents }: Props) => {
  const directSupport = experience === "both";
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"choose" | "collection">("choose");

  const track = (e: "back_her_game_open" | "direct_support_click" | "shop_view") => {
    if (trackEvents) trackEvent(e, { athlete: slug });
  };

  const openChooser = () => {
    setView(directSupport ? "choose" : "collection");
    setOpen(true);
    track("back_her_game_open");
    if (!directSupport) track("shop_view");
  };

  return (
    <>
      <button
        type="button"
        onClick={openChooser}
        aria-label={directSupport ? "Back Her Game: choose how to back " + firstName : "Back Her Game: Shop Her Collection"}
        className="group text-left min-h-[44px] rounded-xl p-4 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-secondary min-w-0"
      >
        <span className="flex flex-wrap items-center gap-2 font-display text-lg uppercase tracking-wide break-words">
          <Trophy className="w-5 h-5 shrink-0" aria-hidden />
          {directSupport ? "Back Her Game" : "Shop Her Collection"}
          <ArrowRight className="w-4 h-4 shrink-0 opacity-70 group-hover:translate-x-0.5 transition-transform" aria-hidden />
        </span>
        <span className="mt-2 block text-sm leading-relaxed break-words">
          {directSupport
            ? `Two ways to back ${firstName}: support her directly or shop her collection.`
            : "Shop her collection and put your purchase behind her brand."}
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90dvh] overflow-y-auto rounded-2xl p-5 sm:p-6">
          {view === "choose" ? (
            <>
              <DialogHeader className="text-left">
                <DialogTitle className="font-display text-2xl uppercase">Back her game</DialogTitle>
                <DialogDescription>Choose how you'd like to back {firstName}.</DialogDescription>
              </DialogHeader>
              <div className="grid gap-3 mt-2">
                {directSupport && (
                  <div className="rounded-xl border-2 border-primary p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      Option 1 · Direct support
                    </p>
                    <h3 className="mt-1 font-display text-xl uppercase flex items-center gap-2">
                      <Heart className="w-5 h-5 shrink-0" aria-hidden /> Back Her Directly
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed">
                      Put resources directly behind her game, her goals and what's next.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        track("direct_support_click");
                        setOpen(false);
                        // Let this dialog fully close first so modals never stack.
                        window.setTimeout(() => window.dispatchEvent(new CustomEvent(OPEN_SUPPORT_CONFIRM_EVENT)), 250);
                      }}
                      className="mt-3 w-full min-h-[48px] rounded-full bg-primary text-primary-foreground font-semibold px-5 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      Back {firstName} Directly
                    </button>
                  </div>
                )}
                <div className="rounded-xl border border-dashed border-foreground/40 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {directSupport ? "Option 2" : "Option"} · Merchandise
                  </p>
                  <h3 className="mt-1 font-display text-xl uppercase flex items-center gap-2">
                    <Shirt className="w-5 h-5 shrink-0" aria-hidden /> Shop Her Collection
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed">
                    Wear her story. Shop merchandise inspired by her game, her voice and what she stands for.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      track("shop_view");
                      setView("collection");
                    }}
                    className="mt-3 w-full min-h-[48px] rounded-full border-2 border-foreground font-semibold px-5 hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    Shop Her Collection
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <DialogHeader className="text-left">
                <DialogTitle className="font-display text-2xl uppercase">Her Collection</DialogTitle>
                <DialogDescription>
                  Wear her story. Merchandise inspired by {firstName}'s game, voice and what she stands for.
                </DialogDescription>
              </DialogHeader>
              {preview && (
                <p className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">
                  Preview only — not visible to the public.
                </p>
              )}
              <HerCollection athleteId={athleteId} athleteName={firstName} preview={preview} />
              {directSupport && (
              <button
                type="button"
                onClick={() => setView("choose")}
                className="mt-2 inline-flex items-center gap-2 min-h-[44px] text-sm font-semibold underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden /> Back to options
              </button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BackHerGameChooser;
